import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Container,
  InputGroup,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  FaBuilding,
  FaEdit,
  FaSave,
  FaTimes,
  FaGlobe,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUsers,
  FaCalendarAlt,
  FaStar,
  FaCheckCircle,
  FaIndustry,
  FaChartLine,
  FaEye,
  FaEyeSlash,
  FaLink,
  FaIdCard,
  FaChartBar,
} from "react-icons/fa";
import { AppDispatch, RootState, useAppDispatch } from "../../store";
import { Icon } from "../../components/common/Icon";
import CompanyAvatar from "../../components/common/CompanyAvatar";
import companiesService, { Company, CompanyUpdate } from "../../features/companies/companiesService";
import { isCompanyOwner } from "../../utils/permissions";
import { useTheme } from "../../context/ThemeContext";
import CompanyFormModal from "../companies/components/CompanyFormModal";

// AgencyData interface'ini Company interface'ine uygun hale getiriyoruz
interface AgencyData extends Company {
  // Company interface'inden gelen alanlar zaten var
  // Ek alanlar için:
  founded_year?: number;
  employee_count?: number;
  banner_url?: string;
  social_media?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
  };
  services?: string[];
  achievements?: string[];
}

const AgencyProfile: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);

  const [agencyData, setAgencyData] = useState<AgencyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isNewCompany, setIsNewCompany] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [formData, setFormData] = useState<Partial<AgencyData>>({});

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const desiredType = params.get("type"); // 'agency' or 'single'

  useEffect(() => {
    const fetchAgencyData = async () => {
      try {
        setIsLoading(true);
        const data = isCompanyOwner(user)
          ? await companiesService.getMyCompany().catch(() => null)
          : await companiesService.getMyAgency().catch(() => null);
        
        // Eğer kayıt sonrası ilk giriş ve henüz company yoksa, boş form ile başlat
        if (!data) {
          const initial: AgencyData = {
            id: -1 as any,
            company_name: "",
            company_type: (desiredType as any) || (isCompanyOwner(user) ? "single" : "agency"),
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            managed_companies: [],
            managing_agencies: [],
          } as unknown as AgencyData;
          setAgencyData(initial);
          setFormData(initial);
          setIsEditing(true);
          setIsNewCompany(true);
          setShowCreateModal(true);
          return;
        }
        
        // API'den gelen veriyi AgencyData formatına dönüştür
        const agencyData: AgencyData = {
          ...data,
          founded_year: data.establishment_date ? new Date(data.establishment_date).getFullYear() : undefined,
          employee_count: data.company_size ? parseInt(data.company_size) : undefined,
          banner_url: data.logo_url, // Geçici olarak logo'yu banner olarak kullan
          social_media: {}, // API'de yok, boş bırak
          services: [], // API'de yok, boş bırak
          achievements: [], // API'de yok, boş bırak
        };
        
        setAgencyData(agencyData);
        setFormData(agencyData);
      } catch (error) {
        console.error("Error fetching agency data:", error);
        setError("Failed to load agency profile. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgencyData();
  }, [location.search]);

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(agencyData || {});
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const wasNewCompany = Boolean(agencyData && (agencyData.id === (-1 as any)));
      // API'ye gönderilecek veriyi hazırla
      const updateData: CompanyUpdate = {
        company_name: formData.company_name,
        description: formData.description,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        postal_code: formData.postal_code,
        industry: formData.industry,
        establishment_date: formData.establishment_date || undefined,
        logo_url: formData.logo_url,
        company_size: formData.company_size,
        is_active: formData.is_active,
      };
      // Eğer henüz company yoksa önce create et
      let updatedData;
      if (agencyData && agencyData.id === (-1 as any)) {
        const createPayload = {
          company_name: updateData.company_name || "",
          company_type: (desiredType as any) || (isCompanyOwner(user) ? "single" : "agency"),
          industry: updateData.industry,
          establishment_date: updateData.establishment_date,
          description: updateData.description,
          website: updateData.website,
          email: updateData.email,
          phone: updateData.phone,
          address: updateData.address,
          city: updateData.city,
          country: updateData.country,
          postal_code: updateData.postal_code,
          tax_id: undefined,
          company_size: updateData.company_size,
          annual_revenue: undefined,
          logo_url: updateData.logo_url,
          is_active: updateData.is_active,
        };
        // create and bind is handled by backend (for owner roles)
        updatedData = await companiesService.createCompany(createPayload as any);
      } else {
        updatedData = isCompanyOwner(user)
          ? await companiesService.updateMyCompany(updateData)
          : await companiesService.updateMyAgency(updateData);
      }
      
      // Güncellenmiş veriyi state'e set et
      const updatedAgencyData: AgencyData = {
        ...updatedData,
        founded_year: updatedData.establishment_date ? new Date(updatedData.establishment_date).getFullYear() : undefined,
        employee_count: updatedData.company_size ? parseInt(updatedData.company_size) : undefined,
        banner_url: updatedData.logo_url,
        social_media: agencyData?.social_media || {},
        services: agencyData?.services || [],
        achievements: agencyData?.achievements || [],
      };

      setAgencyData(updatedAgencyData);
      setFormData(updatedAgencyData);
      setIsEditing(false);
      setIsNewCompany(false);
      setSuccess(wasNewCompany ? "Company created successfully!" : "Company updated successfully!");

      // clear setup required flag once company exists
      localStorage.removeItem('companySetupRequired');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error updating agency:", err);
      setError("Failed to update agency profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof AgencyData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge bg="success" className="status-badge">
        <Icon icon={FaCheckCircle} className="me-1" />
        Active
      </Badge>
    ) : (
      <Badge bg="secondary" className="status-badge">
        <Icon icon={FaEyeSlash} className="me-1" />
        Inactive
      </Badge>
    );
  };

  const renderFormField = (
    label: string,
    field: keyof AgencyData,
    type: string = "text",
    placeholder?: string,
    icon?: any,
    options?: { value: string; label: string }[]
  ) => {
    const value = formData[field];
    
    return (
      <Form.Group className="mb-3">
        <Form.Label className="form-label">
          {icon && <Icon icon={icon} className="me-2" />}
          {label}
        </Form.Label>
        {isEditing ? (
          type === "textarea" ? (
            <Form.Control
              as="textarea"
              rows={4}
              value={String(value || "")}
              onChange={(e) => handleInputChange(field, e.target.value)}
              placeholder={placeholder}
              className="form-control-custom"
            />
          ) : type === "select" && options ? (
            <Form.Select
              value={String(value || "")}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className="form-control-custom"
            >
              <option value="">Select {label.toLowerCase()}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          ) : type === "switch" ? (
            <Form.Check
              type="switch"
              id={`${field}-switch`}
              label={Boolean(value) ? "Active" : "Inactive"}
              checked={Boolean(value)}
              onChange={(e) => handleInputChange(field, e.target.checked)}
              className="form-switch-custom"
            />
          ) : (
            <Form.Control
              type={type}
              value={String(value || "")}
              onChange={(e) => handleInputChange(field, e.target.value)}
              placeholder={placeholder}
              className="form-control-custom"
            />
          )
        ) : (
          <div className="form-display">
            {value ? String(value) : "Not provided"}
          </div>
        )}
      </Form.Group>
    );
  };

  if (isLoading) {
    return (
      <div className="agency-profile-loading">
        <div className="loading-container">
          <div className="loading-spinner">
            <Spinner animation="border" variant="primary" />
          </div>
          <p className="loading-text">Loading agency profile...</p>
        </div>
      </div>
    );
  }

  if (!agencyData) {
    return (
      <div className="agency-profile-error">
        <div className="error-container">
          <Icon icon={FaBuilding} size="3x" className="error-icon" />
          <h4>No Agency Found</h4>
          <p className="text-muted">Agency profile is not configured yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agency-profile">
      {/* Header */}
      <div className="agency-header">
        <Container fluid>
          <Row className="align-items-center">
            <Col md={8}>
                              <div className="agency-info">
                  <div className="agency-logo">
                    <CompanyAvatar
                      companyName={agencyData.company_name}
                      logoUrl={agencyData.logo_url}
                      size="lg"
                      className="logo-image"
                    />
                  </div>
                <div className="agency-details">
                  <h1 className="agency-name">{agencyData.company_name}</h1>
                  <p className="agency-industry">{agencyData.industry}</p>
                  <div className="agency-meta">
                    {getStatusBadge(agencyData.is_active)}
                    {agencyData.founded_year && (
                      <span className="meta-item">
                        <Icon icon={FaCalendarAlt} />
                        Founded {agencyData.founded_year}
                      </span>
                    )}
                    {agencyData.employee_count && (
                      <span className="meta-item">
                        <Icon icon={FaUsers} />
                        {agencyData.employee_count} Employees
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              {!isEditing ? (
                <Button
                  variant="outline-primary"
                  onClick={handleEdit}
                  className="edit-button"
                >
                  <Icon icon={FaEdit} className="me-2" />
                  Edit Company
                </Button>
              ) : (
                <div className="edit-actions">
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="me-2"
                  >
                    <Icon icon={FaTimes} className="me-2" />
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {isNewCompany ? "Saving Company..." : "Saving Changes..."}
                      </>
                    ) : (
                      <>
                        <Icon icon={FaSave} className="me-2" />
                        {isNewCompany ? "Save Company" : "Save Changes"}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Alerts */}
      {error && (
        <Container fluid className="mt-3">
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        </Container>
      )}
      {success && (
        <Container fluid className="mt-3">
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Container>
      )}

      {/* Content */}
      <Container fluid className="mt-4">
        <Row>
          <Col lg={8}>
            {/* Company Details Card */}
            <Card className="profile-card">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaBuilding} className="me-2" />
                  Company Information
                </h5>
                <Row>
                  <Col md={6}>
                    {renderFormField("Company Name", "company_name", "text", "Enter company name", FaBuilding)}
                  </Col>
                  <Col md={6}>
                    {renderFormField("Industry", "industry", "text", "Enter industry", FaIndustry)}
                  </Col>
                </Row>
                {renderFormField("Description", "description", "textarea", "Enter company description")}
              </Card.Body>
            </Card>

            {/* Contact Details Card */}
            <Card className="profile-card mt-4">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaEnvelope} className="me-2" />
                  Contact Information
                </h5>
                <Row>
                  <Col md={6}>
                    {renderFormField("Email Address", "email", "email", "Enter email address", FaEnvelope)}
                  </Col>
                  <Col md={6}>
                    {renderFormField("Phone Number", "phone", "tel", "Enter phone number", FaPhone)}
                  </Col>
                </Row>
                {renderFormField("Website URL", "website", "url", "https://example.com", FaGlobe)}
              </Card.Body>
            </Card>

            {/* Address Information Card */}
            <Card className="profile-card mt-4">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaMapMarkerAlt} className="me-2" />
                  Address Information
                </h5>
                {renderFormField("Street Address", "address", "text", "Enter street address", FaMapMarkerAlt)}
                <Row>
                  <Col md={4}>
                    {renderFormField("City", "city", "text", "Enter city")}
                  </Col>
                  <Col md={4}>
                    {renderFormField("Country", "country", "text", "Enter country")}
                  </Col>
                  <Col md={4}>
                    {renderFormField("Postal Code", "postal_code", "text", "Enter postal code")}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Additional Information Card */}
            <Card className="profile-card mt-4">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaChartBar} className="me-2" />
                  Additional Information
                </h5>
                <Row>
                  <Col md={6}>
                    {renderFormField("Establishment Date", "establishment_date", "date", "", FaCalendarAlt)}
                  </Col>
                  <Col md={6}>
                    {renderFormField("Number of Employees", "company_size", "text", "e.g., 10-50 employees", FaUsers)}
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    {renderFormField("Logo URL", "logo_url", "url", "https://example.com/logo.png", FaLink)}
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">
                        <Icon icon={FaCheckCircle} className="me-2" />
                        Account Status
                      </Form.Label>
                      {isEditing ? (
                        <Form.Check
                          type="switch"
                          id="status-switch"
                          label={formData.is_active ? "Active" : "Inactive"}
                          checked={formData.is_active || false}
                          onChange={(e) => handleInputChange("is_active", e.target.checked)}
                          className="form-switch-custom"
                        />
                      ) : (
                        <div className="d-flex align-items-center">
                          {getStatusBadge(agencyData.is_active)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Agency Status Card */}
            <Card className="profile-card">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaStar} className="me-2" />
                  Account Status
                </h5>
                <div className="status-info">
                  <div className="status-item">
                    <label>Current Status</label>
                    {getStatusBadge(agencyData.is_active)}
                  </div>
                  <div className="status-item">
                    <label>Last Updated</label>
                    <p>{new Date(agencyData.updated_at).toLocaleDateString()}</p>
                  </div>
                  <div className="status-item">
                    <label>Created Date</label>
                    <p>{new Date(agencyData.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Quick Stats Card */}
            <Card className="profile-card mt-4">
              <Card.Body>
                <h5 className="card-title">
                  <Icon icon={FaChartLine} className="me-2" />
                  Quick Statistics
                </h5>
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-value">{agencyData.founded_year || "N/A"}</div>
                    <div className="stat-label">Founded Year</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{agencyData.employee_count || "N/A"}</div>
                    <div className="stat-label">Employees</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{agencyData.managed_companies?.length || 0}</div>
                    <div className="stat-label">Active Clients</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style>{`
        .agency-profile {
          min-height: 100vh;
          background: ${isDark ? "#0f172a" : "#f8f9fa"};
        }

        .agency-profile-loading,
        .agency-profile-error {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${isDark ? "#0f172a" : "#f8f9fa"};
        }

        .loading-container,
        .error-container {
          text-align: center;
        }

        .loading-spinner {
          margin-bottom: 1rem;
        }

        .loading-text {
          color: #6c757d;
          font-size: 1.1rem;
        }

        .error-icon {
          color: ${isDark ? "#94a3b8" : "#6c757d"};
          margin-bottom: 1rem;
        }

        .agency-header {
          background: ${isDark ? "#1e293b" : "#ffffff"};
          border-bottom: 1px solid ${isDark ? "#334155" : "#e9ecef"};
          padding: 2rem 0;
          box-shadow: ${isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 4px rgba(0, 0, 0, 0.1)"};
        }

        .agency-info {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .agency-logo {
          flex-shrink: 0;
        }

        .logo-image {
          width: 80px;
          height: 80px;
          border-radius: 8px;
          object-fit: cover;
          border: 2px solid ${isDark ? "#334155" : "#e9ecef"};
        }

        .agency-details {
          flex: 1;
        }

        .agency-name {
          font-size: 2rem;
          font-weight: 700;
          color: ${isDark ? "#e2e8f0" : "#212529"};
          margin-bottom: 0.5rem;
        }

        .agency-industry {
          font-size: 1.1rem;
          color: ${isDark ? "#94a3b8" : "#6c757d"};
          margin-bottom: 1rem;
        }

        .agency-meta {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #6c757d;
          font-size: 0.9rem;
        }

        .edit-button {
          border-radius: 6px;
          padding: 0.5rem 1.5rem;
          font-weight: 500;
        }

        .edit-actions {
          display: flex;
          gap: 0.5rem;
        }

        .profile-card {
          border: 1px solid ${isDark ? "#334155" : "#e9ecef"};
          border-radius: 8px;
          box-shadow: ${isDark ? "0 2px 10px rgba(0,0,0,0.4)" : "0 2px 4px rgba(0, 0, 0, 0.05)"};
          background: ${isDark ? "#1f2937" : "#ffffff"};
        }

        .card-title {
          font-weight: 600;
          color: ${isDark ? "#e5e7eb" : "#212529"};
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          font-size: 1rem;
        }

        .form-label {
          font-weight: 600;
          color: ${isDark ? "#e2e8f0" : "#495057"};
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
        }

        .form-control-custom {
          border-radius: 6px;
          border: 1px solid ${isDark ? "#475569" : "#ced4da"};
          padding: 0.5rem 0.75rem;
          background: ${isDark ? "#0f172a" : "#ffffff"};
          color: ${isDark ? "#e2e8f0" : "#212529"};
        }

        .form-control-custom::placeholder {
          color: ${isDark ? "#64748b" : "#9aa0a6"};
        }

        .form-control-custom:focus {
          border-color: ${isDark ? "#60a5fa" : "#0d6efd"};
          box-shadow: 0 0 0 0.2rem ${isDark ? "rgba(96,165,250,0.25)" : "rgba(13,110,253,0.25)"};
          background: ${isDark ? "#111827" : "#ffffff"};
          color: ${isDark ? "#e2e8f0" : "#212529"};
        }

        .form-display {
          padding: 0.5rem 0.75rem;
          background-color: ${isDark ? "#111827" : "#f8f9fa"};
          border-radius: 6px;
          border: 1px solid ${isDark ? "#475569" : "#ced4da"};
          color: ${isDark ? "#e2e8f0" : "#212529"};
          min-height: 38px;
          display: flex;
          align-items: center;
        }

        .form-switch-custom {
          padding-left: 2.5rem;
        }

        .form-switch-custom .form-check-input {
          width: 3rem;
          height: 1.5rem;
          margin-left: -2.5rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .stat-item {
          text-align: center;
          padding: 1rem;
          background: ${isDark ? "#111827" : "#f8f9fa"};
          border-radius: 6px;
          border: 1px solid ${isDark ? "#334155" : "#e9ecef"};
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: ${isDark ? "#60a5fa" : "#0d6efd"};
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.8rem;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-info {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .status-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid ${isDark ? "#334155" : "#e9ecef"};
        }

        .status-item:last-child {
          border-bottom: none;
        }

        .status-item label {
          font-weight: 600;
          color: ${isDark ? "#e2e8f0" : "#495057"};
          margin-bottom: 0;
        }

        .status-item p {
          margin-bottom: 0;
          color: ${isDark ? "#94a3b8" : "#6c757d"};
        }

        .status-badge {
          display: flex;
          align-items: center;
          font-size: 0.8rem;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
        }

        .alert {
          border-radius: 6px;
          border: none;
        }

        @media (max-width: 768px) {
          .agency-info {
            flex-direction: column;
            text-align: center;
          }
          
          .agency-meta {
            justify-content: center;
          }
          
          .edit-actions {
            flex-direction: column;
            width: 100%;
          }
          
          .stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Auto-open create modal when user has no company and came with type hint */}
      <CompanyFormModal
        show={showCreateModal}
        onHide={async () => {
          setShowCreateModal(false);
          try {
            const refreshed = isCompanyOwner(user)
              ? await companiesService.getMyCompany()
              : await companiesService.getMyAgency();
            if (refreshed) {
              const updatedAgencyData: AgencyData = {
                ...refreshed,
                founded_year: refreshed.establishment_date ? new Date(refreshed.establishment_date).getFullYear() : undefined,
                employee_count: refreshed.company_size ? parseInt(refreshed.company_size) : undefined,
                banner_url: refreshed.logo_url,
                social_media: {},
                services: [],
                achievements: [],
              } as AgencyData;
              setAgencyData(updatedAgencyData);
              setFormData(updatedAgencyData);
              setIsEditing(false);
              setIsNewCompany(false);
              localStorage.removeItem('companySetupRequired');
            }
          } catch (e) {
            // stay on page; user might have cancelled
          }
        }}
        initialCompanyType={(desiredType as any) || (isCompanyOwner(user) ? "single" : "agency")}
      />
    </div>
  );
};

export default AgencyProfile;
 